/**
 * Transfers between shops: draft, dispatch, receive, and the discrepancies a short receipt raises.
 *
 * **Every mutation here invalidates `stockListTag()`, and that is deliberate.** A transfer moves
 * stock between two shops, so a per-shop stock key would have to be invalidated twice — and the
 * second one is the one that gets forgotten, leaving the receiving shop's screen showing a
 * quantity that has not been true since the van arrived. The whole stock list is the honest
 * blunt instrument here.
 *
 * `movementListTag()` goes with it because every one of these writes ledger rows — except
 * resolving a discrepancy as `lost_written_off`, which writes none at all. Invalidating on that
 * one is harmless (a refetch returns the same rows) and leaving it out would be a special case
 * somebody has to remember.
 */

import { api } from '../api.js';
import {
  discrepancyListTag,
  inTransitTag,
  movementListTag,
  stockListTag,
  transferListTag,
  transferTag,
} from '../tags.js';
import type { Paged, ShopId } from '../../types/api.js';
import type {
  DiscrepancyId,
  DiscrepancyResolution,
  DispatchPreview,
  InTransitStock,
  ReceiveLineInput,
  Transfer,
  TransferDiscrepancy,
  TransferId,
  TransferLineInput,
  TransferStatus,
  TransferSummary,
} from '../../types/stockIntegrity.js';

/** `direction` is relative to `shopId`: what this shop is sending, or what is coming to it. */
export type ListTransfersParams = {
  readonly shopId?: ShopId | undefined;
  readonly status?: TransferStatus | undefined;
  readonly direction?: 'outgoing' | 'incoming' | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type CreateTransferBody = {
  readonly fromShopId?: ShopId | undefined;
  readonly toShopId: ShopId;
  readonly note?: string | undefined;
  readonly lines?: readonly TransferLineInput[] | undefined;
  /** Serialized stock travels by unit id, not by quantity — the serial is what arrives. */
  readonly inventoryUnitIds?: readonly string[] | undefined;
};

export type UpdateTransferBody = {
  readonly note?: string | undefined;
  readonly lines?: readonly TransferLineInput[] | undefined;
  readonly inventoryUnitIds?: readonly string[] | undefined;
};

export type ReceiveTransferBody = {
  readonly lines?: readonly ReceiveLineInput[] | undefined;
  readonly confirmedUnitIds?: readonly string[] | undefined;
};

export const transfersApi = api.injectEndpoints({
  endpoints: (build) => ({
    listTransfers: build.query<Paged<TransferSummary>, ListTransfersParams>({
      query: (params) => ({ url: '/transfers', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [transferListTag()],
    }),

    /** Derived from the lines, never stored. Its own tag because any dispatch or receipt moves it. */
    listInTransit: build.query<InTransitStock[], { shopId?: ShopId | undefined }>({
      query: (params) => ({ url: '/transfers/in-transit', params }),
      providesTags: [inTransitTag()],
    }),

    listDiscrepancies: build.query<
      TransferDiscrepancy[],
      { shopId?: ShopId | undefined; onlyOpen?: boolean | undefined }
    >({
      query: (params) => ({ url: '/transfers/discrepancies', params }),
      providesTags: [discrepancyListTag()],
    }),

    getTransfer: build.query<Transfer, TransferId>({
      query: (id) => `/transfers/${id}`,
      providesTags: (_result, _error, id) => [transferTag(id)],
    }),

    /**
     * What a dispatch will actually do, including whether it can happen at all. Read before the
     * confirmation so the dialog states the effect rather than reporting it afterwards.
     */
    getDispatchPreview: build.query<DispatchPreview, TransferId>({
      query: (id) => `/transfers/${id}/dispatch-preview`,
      // Not tagged to the transfer: availability at the source shop moves with every sale, so this
      // is a point-in-time answer that should be refetched when the dialog opens, not served from
      // a cache that a tag happens not to have invalidated.
      keepUnusedDataFor: 0,
    }),

    /** A draft moves nothing. Stock is validated at dispatch, not here. */
    createTransfer: build.mutation<TransferId, CreateTransferBody>({
      query: (body) => ({ url: '/transfers', method: 'POST', body }),
      invalidatesTags: [transferListTag()],
    }),

    updateTransferDraft: build.mutation<void, { id: TransferId; body: UpdateTransferBody }>({
      query: ({ id, body }) => ({ url: `/transfers/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [transferTag(id), transferListTag()],
    }),

    /** One outward movement per line at the sender. Nothing is written at the destination. */
    dispatchTransfer: build.mutation<void, TransferId>({
      query: (id) => ({ url: `/transfers/${id}/dispatch`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        transferTag(id),
        transferListTag(),
        inTransitTag(),
        stockListTag(),
        movementListTag(),
      ],
    }),

    /**
     * Confirms what physically arrived. Short opens a discrepancy and writes no movement for the
     * gap; over is refused outright, because accepting it would create stock from nothing.
     */
    receiveTransfer: build.mutation<void, { id: TransferId; body: ReceiveTransferBody }>({
      query: ({ id, body }) => ({ url: `/transfers/${id}/receipt`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        transferTag(id),
        transferListTag(),
        inTransitTag(),
        discrepancyListTag(),
        stockListTag(),
        movementListTag(),
      ],
    }),

    /** Drafts only. A dispatched transfer is corrected through discrepancy resolution. */
    cancelTransfer: build.mutation<void, TransferId>({
      query: (id) => ({ url: `/transfers/${id}/cancellation`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [transferTag(id), transferListTag()],
    }),

    resolveDiscrepancy: build.mutation<
      void,
      { discrepancyId: DiscrepancyId; transferId: TransferId; resolution: DiscrepancyResolution; reason: string }
    >({
      query: ({ discrepancyId, resolution, reason }) => ({
        url: `/transfers/discrepancies/${discrepancyId}/resolution`,
        method: 'POST',
        body: { resolution, reason },
      }),
      // `transferId` is carried in the argument purely so this can invalidate the transfer the
      // discrepancy belongs to. The server does not need it; the cache does.
      invalidatesTags: (_result, _error, { transferId }) => [
        transferTag(transferId),
        transferListTag(),
        discrepancyListTag(),
        inTransitTag(),
        stockListTag(),
        movementListTag(),
      ],
    }),
  }),
});

export const {
  useListTransfersQuery,
  useListInTransitQuery,
  useListDiscrepanciesQuery,
  useGetTransferQuery,
  useGetDispatchPreviewQuery,
  useCreateTransferMutation,
  useUpdateTransferDraftMutation,
  useDispatchTransferMutation,
  useReceiveTransferMutation,
  useCancelTransferMutation,
  useResolveDiscrepancyMutation,
} = transfersApi;
